<script>
import axios from 'axios';
import Multiselect from "vue-multiselect";
import {throttle, debounce} from 'lodash';
import BaseLoader from "@/components/BaseLoader";
import InfluencerCard from "@/components/influencerCard";

export default {
  components: {Multiselect, BaseLoader, InfluencerCard},
  beforeMount() {
    this.initFilters();
  },
  mounted() {
    this.submitFilters();

    this.$store.dispatch('user/getCategories').then(() => {});

    window.addEventListener("popstate", this.goBackListener, false);

  },
  beforeDestroy () {
    window.removeEventListener("popstate", this.goBackListener);
  },
  computed: {
    userType () {
      return this.$store.getters['user/userType'];
    },
    paginatedInfluencers() {
      return this.sortedInfluencers.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize);
    },
    categoriesOptions() {
      return this.$store.state.user.categories;
    },
    allInfluencers() {
      const allInfluencersObject = {};
      this.$store.state.search.allInfluencers.forEach(influencer => {
        allInfluencersObject[influencer._id] = influencer
      });

      return allInfluencersObject;
    },
    sortedInfluencers () {
      const localInfluencers = [...this.influencers];
      return localInfluencers.sort((first, second) => {
        const firstValue = first[this.orderBy] && first[this.orderBy][this.getPropertyForSocialNetwork(this.orderBy)];
        const secondValue = second[this.orderBy] && second[this.orderBy][this.getPropertyForSocialNetwork(this.orderBy)];
        return this.compareValues(firstValue, secondValue);
      });
    }
  },
  data() {
    return {
      influencers: [],
      title: "Search for influencers",
      currentPage: 1,
      pageSize: 9,
      isLoading: true,
      orderBy: 'instagram',
      orderLabel: 'Instagram followers',
      errors: {
        age_from: false,
        age_to: false,
        instagram_followers: false,
        facebook_page_likes: false,
        youtube_subscribers: false,
        q: false,
      },

      filters: {}
    };
  },
  watch: {
    filters: {
      handler() {
        if (!this.validateFilters()) {
          return;
        }
        this.updateQueryPath();

        this.submitFilters()
      },
      deep: true
    },
    currentPage() {
      this.updateQueryPath()
    }
  },
  methods: {
    validateFilters() {
      try {
        Object.keys(this.errors).forEach(key => {
          this.errors[key] = false;
        });
        let valid = true;
        if (this.filters.age_from) {
          const ageFrom = parseInt(this.filters.age_from);
          if (ageFrom < 0 || ageFrom > 100) {
            this.errors.age_from = true;
            valid = false
          }
        }
        if (this.filters.age_to) {
          const ageTo = parseInt(this.filters.age_to);
          if (ageTo < 0 || ageTo > 100) {
            this.errors.age_to = true;
            valid = false
          }
        }
        if (this.filters.instagram.followers) {
          const instagramFollowers = parseInt(this.filters.instagram.followers);
          if (instagramFollowers < 0 || instagramFollowers > 1000000000) {
            this.errors.instagram_followers= true;
            valid = false
          }
        }
        if (this.filters.facebook.page_likes) {
          const facebookPageLikes = parseInt(this.filters.facebook.page_likes);
          if (facebookPageLikes < 0 || facebookPageLikes > 1000000000) {
            this.errors.facebook_page_likes= true;
            valid = false
          }
        }
        if (this.filters.youtube.subscribers) {
          const youtubeSubscribers = parseInt(this.filters.youtube.subscribers);
          if (youtubeSubscribers < 0 || youtubeSubscribers > 1000000000) {
            this.errors.youtube_subscribers= true;
            valid = false
          }
        }
        if (this.filters.q) {
          if (this.filters.q.length > 50) {
            this.errors.q= true;
            valid = false
          }
        }

        return valid;
      } catch (e) {
        return false;
      }
    },
    initFilters() {
      let params = new URLSearchParams(window.location.search);
      this.filters = {
        countries: params.get('countries') ? params.get('countries').split(',') : [],
        gender: params.get('gender') ? params.get('gender').split(',') : [],
        age_from: params.get('age_from') ? params.get('age_from') : 18,
        age_to: params.get('age_to') ? params.get('age_to') : 65,

        instagram: {
          followers: params.get('instagram_followers') ?? 0,
        },
        facebook: {
          page_likes: params.get('facebook_page_likes') ?? 0,
        },
        youtube: {
          subscribers: params.get('youtube_subscribers') ?? 0,
        },
        channels: params.get('channels') ? params.get('channels').split(',') : [],
        categories: params.get('categories') ? params.get('categories').split(',') : [],
        q: params.get('q') ?? '',
        include_influencers: params.get('include_influencers') ? params.get('include_influencers').split(',') : [],
      };

      this.currentPage = params.get('currentPage') ?? 1
    },
    // Delay + throttle
    submitFilters: debounce(
        throttle(async function () {
          this.isLoading = true;

          const response = await axios.get(`${this.userType}/influencer${window.location.search}`);
          this.influencers = response.data;

          this.isLoading = false;

        }, 300, {trailing: true}),
        300),

    resetFilters() {
      const newPath = this.$router.currentRoute.path;

      // Update only if this is completely new path
      if (this.$router.currentRoute.fullPath !== newPath) {
        this.$router.push(newPath).then(this.initFilters)
      }
    },

    updateQueryPath() {
      const queryString = this.createQueryPath(this.filters);

      const newPath = this.$router.currentRoute.path + (queryString ? ('?' + queryString) : '');

      // Update only if this is completely new path
      if (this.$router.currentRoute.fullPath !== newPath) {
        this.$router.push(newPath)
      }
    },

    createQueryPath(formInputs) {
      const params = {};

      if (this.filters.countries.length > 0) {
        params.countries = this.filters.countries.join(',')
      }
      if (this.filters.gender.length > 0) {
        params.gender = this.filters.gender.join(',')
      }
      if (this.filters.age_from) {
        params.age_from = this.filters.age_from;
      }
      if (this.filters.age_to) {
        params.age_to = this.filters.age_to;
      }

      ['instagram', 'facebook', 'youtube'].forEach(network => {
        Object.keys(formInputs[network]).forEach(filterKey => {
          if (formInputs[network][filterKey] > 0) {
            params[`${network}_${filterKey}`] = formInputs[network][filterKey]
          }
        })
      });

      if (this.filters.channels.length > 0) {
        params.channels = this.filters.channels.join(',')
      }
      if (this.filters.categories.length > 0) {
        params.categories = this.filters.categories.join(',')
      }
      if (this.filters.q) {
        params.q = this.filters.q
      }
      if (this.filters.include_influencers.length > 0) {
        params.include_influencers = this.filters.include_influencers
      }
      if(this.currentPage > 1){
        params.currentPage = this.currentPage
      }

      return Object.keys(params).map(key => key + '=' + params[key]).join('&');
    },
    goBackListener() {
      const that = this;

      that.initFilters();
      that.submitFilters()
    },
    scrollUp() {
      window.scrollTo(0,0);
    },
    getPropertyForSocialNetwork (socialNetwork) {
      if(socialNetwork === 'instagram') return 'followers';
      if(socialNetwork === 'facebook') return 'page_likes';
      if(socialNetwork === 'youtube') return 'subscribers';
      if(socialNetwork === 'blog') return 'last_30_days_pageviews';
    },
    compareValues (first, second) {
      first = typeof first === 'number' || -1;
      second = typeof second === 'number' || -1;
      if (first < second) {
        return 1;
      }
      if (first > second) {
        return -1;
      }
      return 0;
    },
    updateOrder (socialNetwork) {
      this.orderBy = socialNetwork;
      if(socialNetwork === 'instagram') this.orderLabel = 'Instagram followers';
      if(socialNetwork === 'facebook') this.orderLabel = 'Facebook page likes';
      if(socialNetwork === 'youtube') this.orderLabel = 'Youtube subscribers';
      if(socialNetwork === 'blog') this.orderLabel = 'Blog visits';
    }
  }
};
</script>

<template>

  <div class="row">
    <div class="col-lg-3">
      <div class="card">
        <div class="card-body">
          <div class="d-flex flex-row flex-nowrap justify-content-between">
            <h4 class="card-title mb-4">Filter</h4>
            <a class="btn btn-link d-inline-block p-0" @click="resetFilters">
              <i class="fas fa-undo mr-1"></i>Reset
            </a>
          </div>

          <div class="mb-4">

            <div class="form-group row mb-3">
              <div class="col-12">
                <label class="col-form-label">Influencer Country:</label>
                <multiselect placeholder="Select or Type to search"
                             :close-on-select="false"
                             :preserve-search="true"
                             selectLabel="" selectedLabel="" v-model="filters.countries" :options="['Serbia', 'Bosnia and Herzegovina', 'Slovenia', 'Croatia', 'Montenegro', 'Macedonia']" :multiple="true"></multiselect>
              </div>
            </div>

            <div class="form-group row mb-3">
              <div class="col-12">
                <label class="col-form-label">Influencer Gender:</label>
                <div>
                  <b-form-checkbox
                      v-model="filters.gender"
                      id="genderMale"
                      class="mt-2 d-inline-block"
                      value="male"
                  >Male
                  </b-form-checkbox>

                  <b-form-checkbox
                      v-model="filters.gender"
                      id="genderFemale"
                      class="mt-2 ml-2 d-inline-block"
                      value="female"
                  >Female
                  </b-form-checkbox>
                </div>
              </div>
            </div>

            <div class="form-group row mb-3">
              <div class="col-6">
                <label class="col-form-label">Influencer Age From:</label>
                <input
                    v-model="filters.age_from"
                    type="number"
                    min="1"
                    max="100"
                    autocomplete="off"
                    class="form-control"
                    :class="{'is-invalid': errors.age_from}"
                />
              </div>
              <div class="col-6">
                <label class="col-form-label">Influencer Age To:</label>
                <input
                    v-model="filters.age_to"
                    type="number"
                    min="1"
                    max="100"
                    autocomplete="off"
                    class="form-control"
                    :class="{'is-invalid': errors.age_to}"
                />
              </div>
            </div>

          </div>

          <hr>

          <div class="mt-2 mb-4">
            <div class="form-group row mb-3">
              <div class="col-12">
                <label class="col-form-label">Min Instagram Followers:</label>
                <input
                    v-model="filters.instagram.followers"
                    type="number"
                    min="0"
                    autocomplete="off"
                    class="form-control"
                    :class="{'is-invalid': errors.instagram_followers}"
                />
              </div>
            </div>

            <div class="form-group row mb-3">
              <div class="col-12">
                <label class="col-form-label">Min Facebook Page Likes:</label>
                <input
                    v-model="filters.facebook.page_likes"
                    type="number"
                    min="0"
                    autocomplete="off"
                    class="form-control"
                    :class="{'is-invalid': errors.facebook_page_likes}"
                />
              </div>
            </div>

            <div class="form-group row mb-3">
              <div class="col-12">
                <label class="col-form-label">Min Youtube Subscribers:</label>
                <input
                    v-model="filters.youtube.subscribers"
                    type="number"
                    min="0"
                    autocomplete="off"
                    class="form-control"
                    :class="{'is-invalid': errors.youtube_subscribers}"
                />
              </div>
            </div>
          </div>

          <hr>

          <div class="mt-4">
            <h5 class="font-size-14 mb-3">Channel</h5>

            <b-form-checkbox
                v-model="filters.channels"
                id="instagramChannelInput"
                class="mt-2"
                value="instagram"
            >Instagram
            </b-form-checkbox>

            <b-form-checkbox
                v-model="filters.channels"
                id="facebookChannelInfo"
                class="mt-2"
                value="facebook"
            >Facebook
            </b-form-checkbox>

            <b-form-checkbox
                v-model="filters.channels"
                id="youtubeChannelInfo"
                class="mt-2"
                value="youtube"
            >Youtube
            </b-form-checkbox>

            <b-form-checkbox
                v-model="filters.channels"
                id="blogChannelInfo"
                class="mt-2"
                value="blog"
            >Blog
            </b-form-checkbox>
          </div>

          <div class="mt-3 pt-3">
            <h5 class="font-size-14 mb-3">Choose categories:</h5>
            <div>
              <div class="form-group row mb-3">
                <div class="col-12">
                  <multiselect selectLabel="" selectedLabel="" v-model="filters.categories" class="mb-2" :options="categoriesOptions"
                               :multiple="true"></multiselect>
                </div>
              </div>
            </div>
          </div>

          <hr>

          <div class="mt-2 pt-2" v-if="Object.keys(allInfluencers).length > 0">
            <h5 class="font-size-14 mb-3">Include influencers:</h5>
            <div>
              <div class="form-group row mb-3">
                <div class="col-12">
                  <multiselect selectLabel="" selectedLabel="" v-model="filters.include_influencers" :custom-label="index => allInfluencers[index].username" class="mb-4" :options="Object.keys(allInfluencers)" :multiple="true">
                    <template slot="option" slot-scope="{ option }" v-if="allInfluencers[option]">
                      <img :src="`${allInfluencers[option].profile_picture_url}`" alt
                           class="option__image img-fluid mx-auto " style="border-radius: 50%; width: 30px"/>
                      <span class="option__desc pl-2">
                        <span class="option__title">{{ allInfluencers[option].username }}</span>
                      </span>
                    </template>
                  </multiselect>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-lg-9">
      <div class="row mb-3">
        <div class="col">
          <div class="row">
            <div class="col-12 col-md-6 mt-4 mt-sm-0">
              <div class="search-box mr-2">
                <div class="position-relative">
                  <input
                          v-model="filters.q"
                          type="text"
                          class="form-control border-0"
                          placeholder="Search..."
                          :class="{'is-invalid': errors.q}"
                  />
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-6 order-by d-flex align-items-center justify-content-end">
              <span class="mr-2">Order by:</span>
              <b-dropdown right id="dropdown-1" variant="light">
                <template #button-content>
                  {{ orderLabel }} <span class="font-size-11">&#x25BC;</span>
                </template>
                <b-dropdown-item @click="updateOrder('instagram')">Instagram followers</b-dropdown-item>
                <b-dropdown-item @click="updateOrder('youtube')">Youtube subscribers</b-dropdown-item>
                <b-dropdown-item @click="updateOrder('facebook')">Facebook page likes</b-dropdown-item>
                <b-dropdown-item @click="updateOrder('blog')">Blog visits</b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </div>
      </div>

      <div class="row position-relative">
        <BaseLoader v-if="isLoading" position="absolute" />

        <div v-if="!isLoading && paginatedInfluencers.length === 0" class="text-center flex-row mt-4 font-size-22 flex-grow-1">
          Influencers not found
        </div>


        <div class="col-lg-12 ">
          <InfluencerCard v-for="influencer in paginatedInfluencers" :influencer="influencer" :key="influencer._id" :default-platform="orderBy" ></InfluencerCard>
        </div>
      </div>
      <!-- end row -->

      <div class="row">
        <div class="col-lg-12">
          <b-pagination
              v-if="influencers.length > 0"
              class="justify-content-center"
              pills
              v-model="currentPage"
              @input="scrollUp"
              :total-rows="influencers.length"
              :per-page="this.pageSize"
              aria-controls="my-table"
          ></b-pagination>
        </div>
      </div>
    </div>
  </div>
  <!-- end row -->
</template>

<style>
  .search-box .form-control {
    border-radius: 0.25rem;
  }
</style>
